import { Check } from 'lucide-react';
import {
  Badge,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import type { AgencyClinicResponse } from '../../services/api/types.gen';
import { clinicParticipatesInKinnser } from '../../utils/clinicKeyUtils';

/**
 * A full timestamp, not a date-only string, so `new Date` is the right parse
 * here rather than `parseISOToLocalDate`.
 */
function formatDisconnectedOn(timestamp: string): string {
  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
}

/**
 * The "Mapped to Kinnser?" cell, in three states:
 *
 * - participating: a check, with the WellSky roster and payer counts on hover
 * - disconnected: a Disconnected badge naming the date and the retained key,
 *   so an operator running a cutover can see at a glance which row they changed
 * - never mapped: an em dash
 */
export function ClinicMappedToKinnser({
  clinic,
  insuranceCount,
}: {
  clinic: AgencyClinicResponse;
  insuranceCount: number;
}) {
  if (clinicParticipatesInKinnser(clinic)) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              className='inline-flex items-center justify-center rounded-full text-green-600 hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              aria-label='Mapped to Kinnser'
            >
              <Check className='h-4 w-4' aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent className='space-y-1'>
            <p className='font-medium'>Kinnser Data:</p>
            <p>Physicians: {clinic.physicians?.length || 0}</p>
            <p>Clinicians: {clinic.clinicians?.length || 0}</p>
            <p>Case Managers: {clinic.case_managers?.length || 0}</p>
            <p>Branches: {clinic.branches?.length || 0}</p>
            <p>Insurances: {insuranceCount}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (clinic.kinnser_disconnected_at) {
    const disconnectedOn = formatDisconnectedOn(clinic.kinnser_disconnected_at);
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* A focusable wrapper, like the participating branch: the date and
                the retained key are the point of the badge, so they cannot be
                mouse-only. A bare Badge renders a span and takes no focus. */}
            <button
              type='button'
              className='rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              aria-label='Disconnected from WellSky sync'
            >
              <Badge variant='outline' className='whitespace-nowrap'>
                Disconnected
              </Badge>
            </button>
          </TooltipTrigger>
          <TooltipContent className='space-y-1'>
            <p>
              Disconnected from WellSky sync
              {disconnectedOn ? ` on ${disconnectedOn}` : ''}.
            </p>
            <p>Roger manages this clinic's payers, physicians and staff.</p>
            {clinic.clinic_key && (
              <p>Clinic key {clinic.clinic_key} kept, so it can be restored.</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return <span className='text-muted-foreground'>—</span>;
}

export default ClinicMappedToKinnser;
